import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { AuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BedBadge } from "@/components/FacilityCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Send, MessageSquare, ImagePlus, Loader2, BedDouble, ShieldCheck, Check, X, ClipboardList } from "lucide-react";
import { PaymentTypesField } from "@/components/PaymentTypesField";

export default function DirectorDashboard() {
  const { user } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [adminClaims, setAdminClaims] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [params] = useSearchParams();
  const focusFacility = params.get("facility");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [f, i] = await Promise.all([api.get("/my/facilities"), api.get("/inquiries")]);
      setFacilities(f.data); setInquiries(i.data);
      if (isAdmin) {
        const [c, o] = await Promise.all([api.get("/claims"), api.get("/service-requests")]);
        setAdminClaims(c.data); setOrders(o.data);
      } else {
        const c = await api.get("/my/claims");
        setMyClaims(c.data);
      }
    } catch (e) { console.error("Failed to load director data", e); }
  };
  useEffect(() => { if (user?.role === "director" || user?.role === "admin") load(); }, [user]);
  // Prompt login when an unauthenticated visitor lands here (e.g. from the claim-approval email).
  useEffect(() => { if (!user) setAuthOpen(true); }, [user]);
  // After the director's facilities load, jump straight to the one from the email link.
  useEffect(() => {
    if (focusFacility && facilities.length) {
      const f = facilities.find((x) => x.facility_id === focusFacility);
      if (f) setEditing(f);
    }
  }, [facilities, focusFacility]);

  const requestUpdate = async (f) => {
    try {
      const { data } = await api.post("/beds/request-update", { facility_id: f.facility_id });
      toast.success(`SMS sent to ${data.to} (mocked)`, { description: data.message });
    } catch { toast.error("Could not send request"); }
  };

  const reviewClaim = async (claimId, action) => {
    try {
      await api.post(`/claims/${claimId}/${action}`);
      toast.success(`Claim ${action}d`);
      load();
    } catch { toast.error("Could not update claim"); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F7F6F2]"><Header />
        <div className="text-center py-24 px-4">
          <Building2 className="h-12 w-12 text-[#0077B6] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-medium text-[#1C211F] mb-2">Log in to manage your listing</h1>
          <p className="text-[#5C6660] mb-6">Sign in with the email address your facility was verified under.</p>
          <Button onClick={() => setAuthOpen(true)} className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11 px-8" data-testid="director-login-prompt">Log in</Button>
        </div>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} defaultRole="director" />
      </div>
    );
  }

  if (user && user.role !== "director" && user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#F7F6F2]"><Header />
        <div className="text-center py-24 text-[#5C6660]">This area is for facility directors only.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Header />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl font-medium text-[#1C211F]">{isAdmin ? "Admin Console" : "My Facilities"}</h1>
            <p className="text-[#4A534E] mt-1">{isAdmin ? "Verify claims, manage inquiries and concierge orders." : "Manage listings, availability, and inquiries."}</p>
          </div>
          {!isAdmin && <Link to="/search"><Button className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl"><Plus className="h-4 w-4 mr-1.5" /> Claim a facility</Button></Link>}
        </div>

        <Tabs defaultValue={isAdmin ? "claims" : "facilities"}>
          <TabsList className="bg-[#EFEEE8] mb-6 flex-wrap h-auto">
            {!isAdmin && <TabsTrigger value="facilities" data-testid="tab-facilities">Facilities ({facilities.length})</TabsTrigger>}
            {isAdmin && <TabsTrigger value="claims" data-testid="tab-claims">Claims ({adminClaims.filter((c) => c.status === "pending").length})</TabsTrigger>}
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries ({inquiries.length})</TabsTrigger>
            {!isAdmin && <TabsTrigger value="myclaims" data-testid="tab-myclaims">My Claims ({myClaims.length})</TabsTrigger>}
            {isAdmin && <TabsTrigger value="orders" data-testid="tab-orders">Concierge Orders ({orders.length})</TabsTrigger>}
          </TabsList>

          {isAdmin && (
            <TabsContent value="claims">
              {adminClaims.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-[#E8E6DF] text-[#5C6660]">No claim requests yet.</div>
              ) : (
                <div className="space-y-3" data-testid="admin-claims">
                  {adminClaims.map((c) => (
                    <div key={c.claim_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-[#E3F3ED] flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5 text-[#0077B6]" /></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <span className="font-medium text-[#1C211F]">{c.full_name} <span className="text-[#5C6660] font-normal">· {c.title || "Director"}</span></span>
                            <ClaimStatusBadge status={c.status} />
                          </div>
                          <div className="text-sm text-[#4A534E]">Claiming: <b>{c.facility_name}</b></div>
                          <div className="text-xs text-[#5C6660] mt-0.5">{c.email} · {c.phone || "no phone"}</div>
                          {c.notes && <p className="text-sm text-[#2A312D] mt-2 bg-[#F7F6F2] rounded-xl p-3">{c.notes}</p>}
                          {c.status === "pending" && (
                            <div className="flex gap-2 mt-3">
                              <Button onClick={() => reviewClaim(c.claim_id, "approve")} className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl" data-testid="approve-claim-btn"><Check className="h-4 w-4 mr-1.5" /> Approve & verify</Button>
                              <Button onClick={() => reviewClaim(c.claim_id, "reject")} variant="outline" className="rounded-xl border-[#E8E6DF] text-[#C0392B]" data-testid="reject-claim-btn"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {!isAdmin && (
          <TabsContent value="facilities">
            {facilities.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-[#E8E6DF]">
                <Building2 className="h-10 w-10 text-[#1A365D] mx-auto mb-3" />
                <p className="text-[#4A534E] mb-4">You haven't claimed any facilities yet.</p>
                <Link to="/search"><Button className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl">Find & claim your facility</Button></Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5" data-testid="director-facilities">
                {facilities.map((f) => (
                  <div key={f.facility_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h3 className="font-heading text-lg font-medium text-[#1C211F]">{f.name}</h3>
                        <p className="text-xs uppercase tracking-wide text-[#5C6660] mt-1">{f.facility_type} · {f.city}</p>
                      </div>
                      <BedBadge beds={f.available_beds} tier={f.tier === "free" ? "basic" : f.tier} />
                    </div>
                    <p className="text-xs text-[#5C6660] mb-1 uppercase tracking-wide">Plan: {f.tier || "free"}</p>
                    <p className="text-sm text-[#4A534E] line-clamp-2 mb-4">{f.description || "No description added yet."}</p>
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => setEditing(f)} variant="outline" className="rounded-xl border-[#E8E6DF]" data-testid="edit-facility-btn"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
                      <Button onClick={() => requestUpdate(f)} variant="outline" className="rounded-xl border-[#E8E6DF]" data-testid="request-update-btn"><Send className="h-4 w-4 mr-1.5" /> Request bed update (SMS)</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          )}

          <TabsContent value="inquiries">
            {inquiries.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-[#E8E6DF] text-[#5C6660]">No inquiries yet.</div>
            ) : (
              <div className="space-y-3" data-testid="inquiries-list">
                {inquiries.map((q) => (
                  <div key={q.inquiry_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-xl bg-[#E3F3ED] flex items-center justify-center shrink-0"><MessageSquare className="h-5 w-5 text-[#1A365D]" /></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <span className="font-medium text-[#1C211F]">{q.name}</span>
                          <span className="text-xs text-[#5C6660]">{new Date(q.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="text-sm text-[#4A534E]">{q.email} {q.phone && `· ${q.phone}`}</div>
                        <div className="text-xs text-[#5C6660] mt-1">For: {q.facility_name}{q.care_type && ` · Care: ${q.care_type}`}{q.timeline && ` · ${q.timeline}`}</div>
                        {(q.resident_name || q.relationship || q.payment) && (
                          <div className="text-xs text-[#5C6660] mt-1">
                            {q.resident_name && `Resident: ${q.resident_name}`}{q.resident_age && ` (${q.resident_age})`}
                            {q.relationship && ` · ${q.relationship}`}{q.payment && ` · ${q.payment}`}
                          </div>
                        )}
                        <p className="text-sm text-[#2A312D] mt-2 bg-[#F7F6F2] rounded-xl p-3">{q.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {!isAdmin && (
            <TabsContent value="myclaims">
              {myClaims.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-[#E8E6DF] text-[#5C6660]">You haven't submitted any claims. Find your facility and click "Claim this listing".</div>
              ) : (
                <div className="space-y-3" data-testid="my-claims">
                  {myClaims.map((c) => (
                    <div key={c.claim_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-5 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="font-medium text-[#1C211F]">{c.facility_name}</h3>
                        <p className="text-xs text-[#5C6660] mt-0.5">Submitted {new Date(c.created_at).toLocaleDateString()}</p>
                      </div>
                      <ClaimStatusBadge status={c.status} />
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="orders">
              {orders.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-[#E8E6DF] text-[#5C6660]" data-testid="orders-empty">No concierge setup orders yet.</div>
              ) : (
                <div className="space-y-3" data-testid="service-orders">
                  {orders.map((o) => (
                    <div key={o.request_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-[#E3F3ED] flex items-center justify-center shrink-0"><ClipboardList className="h-5 w-5 text-[#0077B6]" /></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <span className="font-medium text-[#1C211F]">Done-for-you setup — {o.facility_name || "facility"}</span>
                            <span className="text-xs font-semibold text-[#0077B6]">${o.amount} · {o.status}</span>
                          </div>
                          <div className="text-sm text-[#4A534E] mt-0.5">{o.contact_name} · {o.email} · {o.phone || "no phone"}</div>
                          {o.notes && <p className="text-sm text-[#2A312D] mt-2 bg-[#F7F6F2] rounded-xl p-3">{o.notes}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      <EditFacilityDialog facility={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function ClaimStatusBadge({ status }) {
  const map = {
    pending: "bg-[#FEF3C7] text-[#92681A]",
    approved: "bg-[#E3F3ED] text-[#1A365D]",
    rejected: "bg-[#FBE6E6] text-[#C0392B]",
  };
  return <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium capitalize ${map[status] || map.pending}`}>{status}</span>;
}

function EditFacilityDialog({ facility, onClose, onSaved }) {
  const [form, setForm] = useState({ description: "", amenities: "", photos: [], available_beds: "", contact_email: "", website: "", payment_types: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (facility) setForm({
      description: facility.description || "",
      amenities: (facility.amenities || []).join(", "),
      photos: facility.photos || [],
      available_beds: facility.available_beds ?? "",
      contact_email: facility.contact_email || "",
      website: facility.website || "",
      payment_types: facility.payment_types || [],
    });
  }, [facility]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, photos: [...p.photos, data.url] }));
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/facilities/${facility.facility_id}`, {
        description: form.description,
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        photos: form.photos,
        available_beds: form.available_beds === "" ? null : parseInt(form.available_beds, 10),
        contact_email: form.contact_email || null,
        website: form.website || null,
        payment_types: form.payment_types,
      });
      toast.success("Facility updated");
      onSaved();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!facility} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{facility?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><BedDouble className="h-4 w-4" /> Beds available now</Label>
            <Input type="number" min="0" data-testid="edit-beds" value={form.available_beds}
              onChange={(e) => setForm({ ...form, available_beds: e.target.value })} placeholder="e.g. 3" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-contact-email">Inquiry email (Premium — where new inquiries are sent)</Label>
            <Input id="edit-contact-email" type="email" data-testid="edit-contact-email" value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="director@yourfacility.com" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea id="edit-description" rows={4} data-testid="edit-description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe your community, care philosophy, and what makes it special..." className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-amenities">Amenities (comma separated)</Label>
            <Input id="edit-amenities" data-testid="edit-amenities" value={form.amenities}
              onChange={(e) => setForm({ ...form, amenities: e.target.value })} placeholder="Private rooms, Memory care, Garden, 24/7 nursing" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-website">Website {facility?.tier === "premium" ? <span className="text-[#0077B6]">(Premium — shown on your profile)</span> : <span className="text-[#5C6660]">(shows on your profile once Premium)</span>}</Label>
            <Input id="edit-website" data-testid="edit-website" value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://yourfacility.com" className="rounded-xl" />
          </div>
          <PaymentTypesField value={form.payment_types} onChange={(v) => setForm({ ...form, payment_types: v })} />
          <div className="space-y-2">
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-2">
              {form.photos.map((p, i) => <img key={p} src={p} alt={`Facility photo ${i + 1}`} className="h-16 w-16 rounded-xl object-cover" />)}
              <label className="h-16 w-16 rounded-xl border-2 border-dashed border-[#E8E6DF] flex items-center justify-center cursor-pointer hover:border-[#1A365D]" data-testid="upload-photo">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin text-[#5C6660]" /> : <ImagePlus className="h-5 w-5 text-[#5C6660]" />}
                <input type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
            </div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-facility-btn" className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
